import React, { useState, useRef, useEffect } from "react";
import "./LocationsView.css";

// PAGE: Shows stats for each location/shop
const LocationsView = ({ orders, locationMap }) => {
  // STATE: Track selected location to show its orders
  const [selectedLocation, setSelectedLocation] = useState(null);
  // STATE: Pagination for tables
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  // STATE: Track scroll position
  const [scrollPosition, setScrollPosition] = useState(0);

  // REFS: For detecting outside clicks
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const tableContainerRef = useRef(null); // NEW: Ref for table container

  // HELPER: Determine order status based on data
  const getOrderStatus = (order) => {
    if (
      (order.pGhtime && order.pGhtime.trim() !== "") ||
      (order.pJiesuan && order.pJiesuan > 0)
    ) {
      return "completed";
    } else {
      return "pending";
    }
  };

  // Converts a timestamp from China time (UTC+8) to Nairobi time (UTC+3)
  const chinaToNairobi = (chinaTimeString) => {
    if (!chinaTimeString) return "—";

    console.log("Original China time string:", chinaTimeString);

    // 1. Split date and time parts
    const [datePart, timePart] = chinaTimeString.split("T");
    console.log("Date part:", datePart, "Time part:", timePart);

    // 2. Extract numbers
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute, second] = timePart.split(":").map(Number);

    // 3. Convert China time (UTC+8) to UTC
    const dateInUTC = new Date(
      Date.UTC(year, month - 1, day, hour - 8, minute, second)
    );
    console.log("Date in UTC:", dateInUTC.toISOString());

    // 4. Format for Nairobi time
    const nairobiTime = new Intl.DateTimeFormat("en-KE", {
      timeZone: "Africa/Nairobi",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(dateInUTC);

    console.log("Formatted Nairobi time:", nairobiTime);

    return nairobiTime;
  };

  // HELPER: Convert minutes to hours with proper formatting
  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return "0 hrs";

    const hours = minutes / 60;

    if (hours < 1) {
      // Less than 1 hour, show in minutes
      return `${minutes} min`;
    } else if (hours === 1) {
      // Exactly 1 hour
      return "1 hr";
    } else if (hours < 24) {
      // Less than 24 hours, show decimal hours
      return `${hours.toFixed(1)} hrs`;
    } else {
      // 24 hours or more, show in days and hours
      const days = Math.floor(hours / 24);
      const remainingHours = (hours % 24).toFixed(1);

      if (remainingHours === "0.0") {
        return `${days} day${days > 1 ? "s" : ""}`;
      } else {
        return `${days} day${days > 1 ? "s" : ""} ${remainingHours} hrs`;
      }
    }
  };

  // HELPER: Format duration for table display (more detailed)
  const formatDurationForTable = (minutes) => {
    if (!minutes || minutes === 0) return "0 min";

    const hours = minutes / 60;

    if (hours < 1) {
      return `${minutes} min`;
    } else {
      const fullHours = Math.floor(hours);
      const remainingMinutes = Math.round((hours - fullHours) * 60);

      if (remainingMinutes === 0) {
        return `${fullHours} hr${fullHours > 1 ? "s" : ""}`;
      } else {
        return `${fullHours} hr${
          fullHours > 1 ? "s" : ""
        } ${remainingMinutes} min`;
      }
    }
  };

  // FUNCTION: Group all orders by their location
  const groupByLocation = () => {
    const grouped = {};

    if (!orders || orders.length === 0) {
      return grouped;
    }

    orders.forEach((order) => {
      const location =
        order.pShopName || locationMap[order.pCabinetid] || "Unassigned";

      if (!grouped[location]) {
        grouped[location] = [];
      }

      grouped[location].push(order);
    });

    return grouped;
  };

  const locations = groupByLocation();

  // FUNCTION: Calculate stats for a location
  const calculateLocationStats = (locationOrders) => {
    const totalOrders = locationOrders.length;

    const completedOrders = locationOrders.filter(
      (o) =>
        (o.pGhtime && o.pGhtime.trim() !== "") || (o.pJiesuan && o.pJiesuan > 0)
    );

    const totalRevenue = completedOrders.reduce(
      (sum, o) => sum + (o.pJiesuan || 0),
      0
    );

    const avgRevenue =
      completedOrders.length > 0
        ? (totalRevenue / completedOrders.length).toFixed(2)
        : 0;

    const totalDurationMinutes = locationOrders.reduce(
      (sum, o) => sum + (o.billingDuration || 0),
      0
    );

    // Calculate average duration in minutes (original)
    const avgDurationMinutes =
      totalOrders > 0 ? (totalDurationMinutes / totalOrders).toFixed(1) : 0;

    // NEW: Calculate average duration in hours
    const avgDurationHours =
      totalOrders > 0
        ? (totalDurationMinutes / totalOrders / 60).toFixed(2)
        : 0;

    const completionRate =
      totalOrders > 0
        ? ((completedOrders.length / totalOrders) * 100).toFixed(1)
        : 0;

    return {
      totalOrders,
      completedOrders: completedOrders.length,
      totalRevenue,
      avgRevenue: parseFloat(avgRevenue),
      avgDuration: parseFloat(avgDurationMinutes), // Keep original for compatibility
      avgDurationHours: parseFloat(avgDurationHours), // NEW: Average in hours
      completionRate: parseFloat(completionRate),
      orders: locationOrders,
    };
  };

  // Get all location stats
  const locationStats = Object.keys(locations).map((location) => ({
    name: location,
    ...calculateLocationStats(locations[location]),
  }));

  // Pagination logic
  const getCurrentPageData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const totalPages = (data) => Math.ceil(data.length / itemsPerPage);

  // NEW: Smart pagination - show limited page numbers
  const getVisiblePages = (data) => {
    const totalPageCount = totalPages(data);
    const maxVisiblePages = 7; // Show max 7 page numbers
    const current = currentPage;
    const pages = [];

    if (totalPageCount <= maxVisiblePages) {
      // If total pages is less than max visible, show all
      return Array.from({ length: totalPageCount }, (_, i) => i + 1);
    }

    // Always show first page
    pages.push(1);

    // Calculate start and end of visible pages
    let startPage = Math.max(2, current - 2);
    let endPage = Math.min(totalPageCount - 1, current + 2);

    // Adjust if we're near the start
    if (current <= 3) {
      endPage = Math.min(totalPageCount - 1, maxVisiblePages - 1);
    }

    // Adjust if we're near the end
    if (current >= totalPageCount - 2) {
      startPage = Math.max(2, totalPageCount - (maxVisiblePages - 2));
    }

    // Add ellipsis after first page if needed
    if (startPage > 2) {
      pages.push("...");
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPageCount - 1) {
      pages.push("...");
    }

    // Always show last page
    if (totalPageCount > 1) {
      pages.push(totalPageCount);
    }

    return pages;
  };

  // UPDATED: Better scroll to top function (only for modal content)
  const scrollToTop = () => {
    if (!selectedLocation) return; // Only scroll in modal view

    const scrollOptions = { top: 0, behavior: "smooth" };

    // Method 1: Scroll the table container if it exists
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo(scrollOptions);
    }

    // Method 2: Scroll the modal content area
    if (contentRef.current) {
      contentRef.current.scrollTo(scrollOptions);
    }

    // Method 3: Use setTimeout as backup for any rendering delays
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo(scrollOptions);
      }
    }, 100);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top when page changes (only in modal)
    if (selectedLocation) {
      scrollToTop();
    }
  };

  // FUNCTION: Save scroll position before opening modal
  const saveScrollPosition = () => {
    setScrollPosition(window.pageYOffset || document.documentElement.scrollTop);
  };

  // FUNCTION: Restore scroll position when closing modal
  const restoreScrollPosition = () => {
    window.scrollTo({
      top: scrollPosition,
      behavior: "instant", // Use instant to avoid animation conflict
    });
  };

  // FUNCTION: Handle location card click
  const handleLocationClick = (location) => {
    saveScrollPosition(); // Save position before opening modal
    setSelectedLocation(location);
    setCurrentPage(1);
  };

  // FUNCTION: Handle back to locations grid
  const handleBackToGrid = () => {
    setSelectedLocation(null);
    setCurrentPage(1);
    // Restore scroll position after a brief delay to ensure modal is closed
    setTimeout(restoreScrollPosition, 10);
  };

  // FUNCTION: Handle location change from dropdown
  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value || null);
    setCurrentPage(1);
  };

  // Get stats for selected location
  const getStatsForLocation = (locationName) => {
    return locationStats.find((loc) => loc.name === locationName);
  };

  // EFFECT: Handle outside clicks to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectedLocation &&
        overlayRef.current &&
        !contentRef.current.contains(event.target)
      ) {
        handleBackToGrid();
      }
    };

    if (selectedLocation) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedLocation]);

  // EFFECT: Prevent background scroll when modal is open
  useEffect(() => {
    if (selectedLocation) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedLocation]);

  // Render the page
  if (!selectedLocation) {
    return (
      <div className="locations-view">
        <div className="locations-header">
          <h2 className="lsection-title">
            <i className="fas fa-map-pin pulse"></i> Location Metrics
          </h2>
          <p className="section-subtitle">Performance across all locations</p>
        </div>

        {/* Locations Grid */}
        <div className="locations-grid">
          {locationStats.map((location, idx) => (
            <div
              key={location.name}
              className="location-card"
              onClick={() => handleLocationClick(location.name)}
            >
              <div
                className="card-glow"
                style={{
                  background: `linear-gradient(135deg, #${Math.floor(
                    Math.random() * 16777215
                  ).toString(16)} 0%, #${Math.floor(
                    Math.random() * 16777215
                  ).toString(16)} 100%)`,
                }}
              ></div>
              <div className="card-content">
                <div className="card-header">
                  <div
                    className="card-icon"
                    style={{
                      background: `linear-gradient(135deg, #${Math.floor(
                        Math.random() * 16777215
                      ).toString(16)} 0%, #${Math.floor(
                        Math.random() * 16777215
                      ).toString(16)} 100%)`,
                    }}
                  >
                    <i className="fas fa-store"></i>
                  </div>
                </div>
                <div className="lcard-body">
                  <h3 className="lcard-title">{location.name}</h3>
                  <div className="lcard-stats">
                    <div className="lstat-item">
                      <span className="lstat-value">
                        {location.totalOrders}
                      </span>
                      <span className="lstat-label">Total Orders</span>
                    </div>
                    <div className="lstat-item">
                      <span className="lstat-value">
                        {location.completedOrders}
                      </span>
                      <span className="stat-label">Completed</span>
                    </div>
                    <div className="lstat-item">
                      <span className="lstat-value">
                        Ksh {location.totalRevenue.toLocaleString()}
                      </span>
                      <span className="lstat-label">Revenue</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Selected location view - MODAL STYLE
  const selectedStats = getStatsForLocation(selectedLocation);
  const currentData = getCurrentPageData(selectedStats?.orders || []);
  const totalPageCount = totalPages(selectedStats?.orders || []);
  const visiblePages = getVisiblePages(selectedStats?.orders || []);

  return (
    <>
      {/* Overlay for outside clicks */}
      <div
        ref={overlayRef}
        className="location-overlay"
        onClick={handleBackToGrid}
      ></div>

      <div className="locations-view selected-location-modal" ref={contentRef}>
        {/* Header with Navigation */}
        <div className="detail-header">
          <div className="header-left">
            <button className="back-btn" onClick={handleBackToGrid}>
              <i className="fas fa-arrow-left"></i> Back to Locations
            </button>
          </div>
          <div className="header-center">
            <h2 className="detail-title">
              <i className="fas fa-store"></i>
              {selectedLocation}
              <span className="record-count">
                ({selectedStats?.totalOrders || 0} orders)
              </span>
            </h2>
          </div>
          <div className="header-right">
            <div className="location-selector">
              <select
                value={selectedLocation || ""}
                onChange={handleLocationChange}
                className="location-dropdown"
              >
                <option value="">Switch Location</option>
                {locationStats.map((location) => (
                  <option key={location.name} value={location.name}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Location Stats Overview - Hidden on mobile */}
        <div className="location-overview desktop-only">
          <div className="overview-card">
            <div
              className="overview-icon"
              style={{
                background: `linear-gradient(135deg, #5e52e0 0%, #5448fa 100%)`,
              }}
            >
              <i className="fas fa-shopping-cart"></i>
            </div>
            <div className="overview-content">
              <span className="overview-value">
                {selectedStats?.totalOrders || 0}
              </span>
              <span className="overview-label">Total Orders</span>
            </div>
          </div>
          <div className="overview-card">
            <div
              className="overview-icon"
              style={{
                background: `linear-gradient(135deg, #4b6d22 0%, #5a8c2e 100%)`,
              }}
            >
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="overview-content">
              <span className="overview-value">
                {selectedStats?.completedOrders || 0}
              </span>
              <span className="overview-label">Completed</span>
            </div>
          </div>
          <div className="overview-card">
            <div
              className="overview-icon"
              style={{
                background: `linear-gradient(135deg, #ef2f6e 0%, #ff4d8d 100%)`,
              }}
            >
              <i className="fas fa-clock"></i>
            </div>
            <div className="overview-content">
              <span className="overview-value">
                {selectedStats
                  ? selectedStats.totalOrders - selectedStats.completedOrders
                  : 0}
              </span>
              <span className="overview-label">Pending</span>
            </div>
          </div>
          <div className="overview-card">
            <div
              className="overview-icon"
              style={{
                background: `linear-gradient(135deg, #581f66 0%, #6b2780 100%)`,
              }}
            >
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div className="overview-content">
              <span className="overview-value">
                Ksh {selectedStats?.totalRevenue.toLocaleString() || 0}
              </span>
              <span className="overview-label">Revenue</span>
            </div>
          </div>
          {/* NEW: Average Duration Card */}
          <div className="overview-card">
            <div
              className="overview-icon"
              style={{
                background: `linear-gradient(135deg, #311a64 0%, #3d1f7a 100%)`,
              }}
            >
              <i className="fas fa-hourglass-half"></i>
            </div>
            <div className="overview-content">
              <span className="overview-value">
                {selectedStats
                  ? formatDuration(selectedStats.avgDuration)
                  : "0 hrs"}
              </span>
              <span className="overview-label">Avg Duration</span>
            </div>
          </div>
          {/* NEW: Average Revenue Card */}
          <div className="overview-card">
            <div
              className="overview-icon"
              style={{
                background: `linear-gradient(135deg, #b52a6b 0%, #d4347a 100%)`,
              }}
            >
              <i className="fas fa-chart-line"></i>
            </div>
            <div className="overview-content">
              <span className="overview-value">
                Ksh {selectedStats?.avgRevenue || 0}
              </span>
              <span className="overview-label">Avg Revenue</span>
            </div>
          </div>
        </div>

        {/* Data Table - Always visible */}
        <div className="data-section">
          <div className="table-container" ref={tableContainerRef}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Cabinet ID</th>
                  <th>Location</th>
                  <th>Borrow Time</th>
                  <th>Duration</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((order, index) => {
                  const status = getOrderStatus(order);
                  const statusLabel =
                    status === "completed" ? "Completed" : "Pending";
                  const globalIndex =
                    (currentPage - 1) * itemsPerPage + index + 1;

                  return (
                    <tr key={order.id || index} className="table-row">
                      <td>
                        <span className="order-id">
                          #{order.pOrderid || order.pAliorderid || globalIndex}
                        </span>
                      </td>
                      <td>{order.pCabinetid}</td>
                      <td>
                        <div className="location-cell">
                          <i className="fas fa-map-marker-alt"></i>
                          {order.pShopName || "Unassigned"}
                        </div>
                      </td>
                      {/* <td>
                        <div className="time-cell">
                          {new Date(order.pBorrowtime).toLocaleDateString()}
                          <span className="time-small">
                            {new Date(order.pBorrowtime).toLocaleTimeString()}
                          </span>
                        </div>
                      </td> */}
                      <td>
                        <div className="time-cell">
                          {chinaToNairobi(order.pBorrowtime)}
                        </div>
                      </td>
                      <td>
                        <span className="duration-badge">
                          {formatDurationForTable(order.billingDuration || 0)}{" "}
                          {/* UPDATED: Use hours formatting */}
                        </span>
                      </td>
                      <td>
                        <span className="amount-cell">
                          Ksh {(order.pJiesuan || 0).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${status}`}>
                          <i
                            className={`fas fa-${
                              status === "completed" ? "check" : "clock"
                            }`}
                          ></i>
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {(!selectedStats || selectedStats.orders.length === 0) && (
              <div className="no-data">
                <i className="fas fa-inbox"></i>
                <h3>No Orders Found</h3>
                <p>
                  No orders available for {selectedLocation} in the selected
                  date range.
                </p>
              </div>
            )}
          </div>

          {/* UPDATED Pagination */}
          {selectedStats && selectedStats.orders.length > 0 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="fas fa-chevron-left"></i> Previous
              </button>

              <div className="page-numbers">
                {visiblePages.map((page, index) =>
                  page === "..." ? (
                    <span key={`ellipsis-${index}`} className="page-ellipsis">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`page-btn ${
                        currentPage === page ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPageCount}
              >
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LocationsView;
